/**
 * The kiosk's brain: decisions, the offline queue, sync and the wall code. No React here; the screen subscribes.
 *
 * Scanner: online → `POST /kiosk/scan`. Offline, or no answer within 2 s → `decideEntry` from `@helmr/checkin` with the cached
 * key set and roster, then `OfflineCheckinQueue`, persisting `snapshot()` after every change. Sync every 30 s and on reconnect.
 * Wall: shows a `K1` code from the wall secret; it never asks for the key set or the roster.
 */
import {
  OfflineCheckinQueue, ReplayGuard, decideEntry, generateKioskCode, verifyMemberCode, windowIndex,
  type BranchHours, type CheckinEvent, type KeySet, type Plan,
} from "@helmr/checkin";
import { idKindOf, localDateOf, newId } from "@helmr/contracts";
import { createClient, isApiError, type ApiClient } from "../../api/client";
import {
  getConfig, getKeyset, getRoster, getWallSecret, postScan, postSync,
  type KioskConfig, type ScanResponse, type WeeklyHours,
} from "../../api/kiosk";
import { reasonText, verdictFor, type ReasonContext, type ResultTone } from "./reasons";
import type { KioskCredential, KioskStore, RosterCache, WallSecretCache } from "./store";

export const SCAN_TIMEOUT_MS = 2_000;
export const RESULT_MS = 3_000;
export const SYNC_EVERY_MS = 30_000;
export const REFRESH_EVERY_MS = 10 * 60_000;
export const FULL_ROSTER_EVERY_MS = 24 * 3_600_000;
export const SAME_CODE_MS = 5_000;
export const SKEW_WARN_MS = 2 * 60_000;
const DEFAULT_WINDOW = { windowSeconds: 30, skewWindows: 1, wallMaxAgeWindows: 1 };

export interface ScanResult {
  /** Increments per scan, so the screen can restart its 3 s timer. */
  seq: number;
  tone: ResultTone;
  headline: string;
  notes: string[];
  /** Null when the kiosk does not know the name (a staff code scanned offline). */
  name: string | null;
  photoUrl: string | null;
  staff: boolean;
  /** Decided offline and waiting to sync. */
  queued: boolean;
}

export type Phase = "loading" | "ready" | "needs_network" | "revoked";

export interface KioskView {
  phase: Phase;
  credential: Pick<KioskCredential, "kioskId" | "label" | "mode" | "gymName" | "branchId">;
  config: KioskConfig | null;
  online: boolean;
  queued: number;
  /** A sync failure that was not just "offline". */
  syncError: string | null;
  /** Null until the roster has loaded once. */
  rosterCount: number | null;
  /** Device clock minus server clock, when known. */
  clockSkewMs: number | null;
  result: ScanResult | null;
  wall: { code: string; windowEndsAt: number } | null;
  /** Why the wall shows no code. */
  wallProblem: "expired" | "unavailable" | null;
}

export interface EngineDeps {
  credential: KioskCredential;
  store: KioskStore;
  client: ApiClient;
  now: () => number;
  /** For "Plan expired on 12 Oct". */
  locale?: string;
  /** Called after the stored credential is wiped. */
  onRevoked?: () => void;
  isOnline?: () => boolean;
}

export interface CreateEngineOptions { baseUrl: () => string; credential: KioskCredential; store: KioskStore; now?: () => number; locale?: string; onRevoked?: () => void }

/** A kiosk client plus the engine that owns it, wired so a `401 invalid_kiosk` anywhere wipes the kiosk. */
export function createKioskEngine(o: CreateEngineOptions): KioskEngine {
  let engine: KioskEngine | null = null;
  const client = createClient({
    baseUrl: o.baseUrl,
    getToken: () => o.credential.token,
    onUnauthenticated: () => {},
    kiosk: { onInvalidKiosk: () => void engine?.revoke() },
  });
  engine = new KioskEngine({ credential: o.credential, store: o.store, client, now: o.now ?? Date.now, locale: o.locale, onRevoked: o.onRevoked });
  return engine;
}

const isAbort = (e: unknown) => e instanceof DOMException && e.name === "AbortError";
/** No answer: network down, timeout, or the server is struggling. These fall back to the offline decision. */
const isUnreachable = (e: unknown) => isAbort(e) || (isApiError(e) && (e.status === 0 || e.status >= 500 || e.status === 429));

export class KioskEngine {
  private listeners = new Set<() => void>();
  private view: KioskView;
  private config: KioskConfig | null = null;
  private keyset: KeySet | null = null;
  private roster: RosterCache | null = null;
  private wallSecret: WallSecretCache | null = null;
  private queue: OfflineCheckinQueue;
  private replay: ReplayGuard;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private intervals: ReturnType<typeof setInterval>[] = [];
  private wallTimer: ReturnType<typeof setTimeout> | null = null;
  private resultTimer: ReturnType<typeof setTimeout> | null = null;
  private recent = new Map<string, number>();
  private seq = 0;
  private syncing = false;
  private dead = false;
  /** Bumped by `stop()` so a `start()` still awaiting its reads (React StrictMode mounts twice) does not install timers. */
  private gen = 0;
  private readonly locale: string;
  private readonly onWindowOnline = () => { this.setOnline(true); void this.syncNow(); void this.refresh(); };
  private readonly onWindowOffline = () => this.setOnline(false);

  constructor(private d: EngineDeps) {
    this.locale = d.locale ?? ((typeof navigator !== "undefined" && navigator.language) || "en-GB");
    this.queue = new OfflineCheckinQueue(d.credential.kioskId);
    this.replay = new ReplayGuard(DEFAULT_WINDOW.windowSeconds, DEFAULT_WINDOW.skewWindows);
    const { kioskId, label, mode, gymName, branchId } = d.credential;
    this.view = {
      phase: "loading", credential: { kioskId, label, mode, gymName, branchId }, config: null, online: d.isOnline ? d.isOnline() : true,
      queued: 0, syncError: null, rosterCount: null, clockSkewMs: null, result: null, wall: null, wallProblem: null,
    };
  }

  /* ---------- state ---------- */
  getState = (): KioskView => this.view;
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  private set(patch: Partial<KioskView>) {
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((l) => l());
  }
  private setOnline(online: boolean) { if (this.view.online !== online) this.set({ online }); }

  /* ---------- lifecycle ---------- */
  async start(): Promise<void> {
    if (this.dead) return;
    this.pause();
    const gen = this.gen;
    const { store } = this.d;
    const [config, keyset, roster, wall, saved] = await Promise.all([store.read("config"), store.read("keyset"), store.read("roster"), store.read("wall"), store.read("queue")]);
    if (gen !== this.gen || this.dead) return;
    [this.config, this.keyset, this.roster, this.wallSecret] = [config, keyset, roster, wall];
    this.queue = OfflineCheckinQueue.restore(this.d.credential.kioskId, saved ?? []);
    this.applyWindow();
    this.set({ config: this.config, queued: this.queue.snapshot().length, rosterCount: this.roster ? Object.keys(this.roster.members).length : null, phase: this.config ? "ready" : "loading" });

    if (typeof window !== "undefined") {
      window.addEventListener("online", this.onWindowOnline);
      window.addEventListener("offline", this.onWindowOffline);
    }
    this.intervals.push(setInterval(() => void this.syncNow(), SYNC_EVERY_MS), setInterval(() => void this.refresh(), REFRESH_EVERY_MS));
    if (this.d.credential.mode === "wall") await this.tickWall(); // a cached secret shows a code before the network answers
    await this.refresh();
    if (this.d.credential.mode === "scanner") void this.syncNow();
  }

  /** Stops timers and listeners. `start()` may be called again. */
  stop(): void { this.pause(); }

  private pause(): void {
    this.gen++;
    this.intervals.forEach(clearInterval);
    this.timers.forEach(clearTimeout);
    this.intervals = [];
    this.timers = [];
    if (this.wallTimer) clearTimeout(this.wallTimer);
    if (this.resultTimer) clearTimeout(this.resultTimer);
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.onWindowOnline);
      window.removeEventListener("offline", this.onWindowOffline);
    }
  }

  /** `401 invalid_kiosk`: the credential, keys, roster and queue go, and the screen says the kiosk was removed. */
  async revoke(): Promise<void> {
    if (this.view.phase === "revoked") return;
    this.dead = true;
    this.pause();
    this.config = this.keyset = this.roster = this.wallSecret = null;
    this.queue = new OfflineCheckinQueue(this.d.credential.kioskId);
    this.set({ phase: "revoked", config: null, result: null, wall: null, queued: 0, rosterCount: null, syncError: null });
    try { await this.d.store.wipe(); } finally { this.d.onRevoked?.(); }
  }

  /* ---------- refreshing what the kiosk holds ---------- */
  private applyWindow() {
    const w = this.config?.window ?? DEFAULT_WINDOW;
    this.replay = new ReplayGuard(w.windowSeconds, w.skewWindows);
  }

  private noteServerTime(serverTime: number) {
    this.set({ clockSkewMs: this.d.now() - serverTime });
  }

  /** Config, then (scanner) key set and roster, or (wall) the wall secret. Failures keep what is cached. */
  async refresh(): Promise<void> {
    if (this.dead) return;
    const { store, client, credential } = this.d;
    try {
      this.config = await getConfig(client);
      this.noteServerTime(this.config.serverTime);
      this.applyWindow();
      await store.write("config", this.config);
      this.set({ config: this.config, phase: "ready", online: true });
    } catch (e) {
      if (this.dead) return;
      if (isUnreachable(e)) this.setOnline(false);
      else this.set({ syncError: isApiError(e) ? e.message : "Could not load the kiosk settings" });
      if (!this.config) this.set({ phase: isUnreachable(e) ? "needs_network" : "loading" });
      return;
    }
    if (credential.mode === "wall") { await this.refreshWallSecret(); return; }
    try {
      const ks = await getKeyset(client);
      this.keyset = { keys: ks.keys };
      this.noteServerTime(ks.serverTime);
      await store.write("keyset", this.keyset);
      await this.refreshRoster();
      if (!this.dead) this.set({ syncError: null });
    } catch (e) {
      if (this.dead) return;
      if (isUnreachable(e)) this.setOnline(false);
      else this.set({ syncError: isApiError(e) ? e.message : "Could not refresh the member list" });
    }
  }

  private async refreshRoster(): Promise<void> {
    const now = this.d.now();
    const full = !this.roster || now - this.roster.fullAt > FULL_ROSTER_EVERY_MS;
    const since = full ? null : this.roster!.asOf;
    const members: RosterCache["members"] = full ? {} : { ...this.roster!.members };
    let cursor: string | null = null;
    let asOf: number | null = null;
    for (;;) {
      const page = await getRoster(this.d.client, { since, cursor, limit: 500 });
      asOf ??= page.asOf;
      for (const m of page.members) {
        if (m.active) members[m.id] = { name: m.name, photoUrl: m.photoUrl, plans: m.plans, balanceDue: m.balanceDue };
        else delete members[m.id];
      }
      if (!page.nextCursor) break;
      cursor = page.nextCursor;
    }
    this.roster = { members, asOf: asOf ?? now, fullAt: full ? now : this.roster!.fullAt };
    await this.d.store.write("roster", this.roster);
    this.set({ rosterCount: Object.keys(members).length });
  }

  /* ---------- scanning ---------- */
  async scan(raw: string): Promise<void> {
    const code = raw.trim();
    if (this.dead || !code || this.d.credential.mode !== "scanner") return;
    const now = this.d.now();
    // The same QR stays in front of the camera for a moment: one scan, not ten.
    for (const [c, at] of this.recent) if (now - at > SAME_CODE_MS) this.recent.delete(c);
    if (this.recent.has(code)) return;
    this.recent.set(code, now);

    const ac = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<"timeout">((resolve) => { timer = setTimeout(() => { ac.abort(); resolve("timeout"); }, SCAN_TIMEOUT_MS); });
    try {
      const res = await Promise.race([postScan(this.d.client, code, ac.signal), timeout]);
      if (res === "timeout") throw new DOMException("timeout", "AbortError");
      if (this.dead) return;
      this.setOnline(true);
      this.showServerResult(res);
    } catch (e) {
      if (this.dead) return;
      if (isUnreachable(e)) {
        this.setOnline(false);
        await this.decideOffline(code);
      } else {
        this.showError(e);
      }
    } finally {
      clearTimeout(timer);
    }
  }

  private ctx(personId?: string): ReasonContext {
    const tz = this.config?.branch.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const plans = personId ? this.roster?.members[personId]?.plans : undefined;
    return { plans, onDate: localDateOf(this.d.now(), tz), locale: this.locale };
  }

  private showResult(r: Omit<ScanResult, "seq">) {
    if (this.resultTimer) clearTimeout(this.resultTimer);
    this.set({ result: { ...r, seq: ++this.seq } });
    this.resultTimer = setTimeout(() => this.set({ result: null }), RESULT_MS);
  }

  private showServerResult(res: ScanResponse) {
    if (res.subject === "staff") {
      this.showResult({ tone: "ok", headline: res.punched === false ? "Already clocked" : "Punch recorded", notes: [], name: res.person.name, photoUrl: res.person.photoUrl, staff: true, queued: false });
      return;
    }
    const v = verdictFor(res.decision, res.colour, res.reasons, this.ctx(res.person.id));
    this.showResult({ ...v, name: res.person.name, photoUrl: res.person.photoUrl, staff: false, queued: false });
  }

  private showError(e: unknown) {
    if (isApiError(e) && e.status === 409) {
      this.showResult({ tone: "warn", headline: "Already scanned", notes: ["This code was just used. Ask for a fresh one."], name: null, photoUrl: null, staff: false, queued: false });
      return;
    }
    if (isApiError(e) && e.status === 422) {
      this.showResult({ tone: "deny", headline: "Code not recognised", notes: ["Ask the member to open their code again."], name: null, photoUrl: null, staff: false, queued: false });
      return;
    }
    this.showResult({ tone: "deny", headline: "Could not check in", notes: [isApiError(e) ? e.message : "Try again."], name: null, photoUrl: null, staff: false, queued: false });
  }

  /** Verify with the cached keys, decide from the cached roster, queue. Nothing here talks to the server. */
  private async decideOffline(code: string): Promise<void> {
    const cfg = this.config;
    if (!this.keyset || !cfg) {
      this.showResult({ tone: "deny", headline: "Not ready", notes: ["Connect this kiosk to the internet once so it can download its keys."], name: null, photoUrl: null, staff: false, queued: false });
      return;
    }
    const now = this.d.now();
    const w = cfg.window;
    const v = await verifyMemberCode(code, this.keyset, now, { windowSeconds: w.windowSeconds, skewWindows: w.skewWindows });
    if (!v.ok) {
      const expired = v.reason === "unknown_key";
      this.showResult({
        tone: "deny", headline: "Code not recognised",
        notes: [expired ? "This kiosk's keys may have expired. Connect it to the internet." : "Ask the member to open their code again."],
        name: null, photoUrl: null, staff: false, queued: false,
      });
      return;
    }
    if (!this.replay.checkAndRecord(code, now)) {
      this.showResult({ tone: "warn", headline: "Already scanned", notes: ["This code was just used. Ask for a fresh one."], name: null, photoUrl: null, staff: false, queued: false });
      return;
    }

    const staff = idKindOf(v.memberId) === "staff";
    let decision: "allow" | "deny" = "allow";
    let result: Omit<ScanResult, "seq">;
    if (staff) {
      result = { tone: "ok", headline: "Punch saved", notes: ["It will sync when the kiosk is back online."], name: null, photoUrl: null, staff: true, queued: true };
    } else {
      const entry = this.roster?.members[v.memberId];
      const d = this.decideFor(v.memberId, cfg, now);
      decision = d.result;
      const ctx = this.ctx(v.memberId);
      const verdict = verdictFor(d.result, d.colour, d.reasons, ctx);
      result = { ...verdict, name: entry?.name ?? null, photoUrl: entry?.photoUrl ?? null, staff: false, queued: true };
    }

    const event: CheckinEvent = { eventId: newId("checkin", { now }), kioskId: this.d.credential.kioskId, branchId: this.d.credential.branchId, code, scannedAtMs: now, decision };
    if (!this.queue.append(event)) {
      this.showResult({ tone: "warn", headline: "Already scanned", notes: ["This code was just used. Ask for a fresh one."], name: result.name, photoUrl: null, staff, queued: false });
      return;
    }
    await this.persistQueue();
    this.showResult(result);
  }

  private decideFor(memberId: string, cfg: KioskConfig, now: number) {
    const entry = this.roster?.members[memberId];
    const onDate = localDateOf(now, cfg.branch.timeZone);
    const nowLocalTime = localTimeOf(now, cfg.branch.timeZone);
    // Not on the roster = no plan that could admit them here. The roster is built that way (device-routes.ts: "least data").
    const plans: Plan[] = entry?.plans ?? [];
    return decideEntry({
      member: { id: memberId, balanceDuePaise: entry?.balanceDue ? 1 : 0 },
      plans,
      branchId: cfg.branch.id,
      onDate,
      nowLocalTime,
      // The kiosk cannot count earlier visits at other branches. The server recomputes on sync and flags a mismatch for the owner.
      visitCountsAtBranch: {},
      branchHours: hoursAt(cfg.branch.hours, onDate, nowLocalTime),
      settings: cfg.entry,
    });
  }

  /* ---------- sync ---------- */
  private async persistQueue() {
    await this.d.store.write("queue", this.queue.snapshot());
    this.set({ queued: this.queue.snapshot().length });
  }

  /** Sends queued scans in pages until the queue is empty, a page fails, or the server acknowledges nothing. */
  async syncNow(): Promise<void> {
    if (this.dead || this.syncing || this.d.credential.mode !== "scanner") return;
    if (this.queue.pending().length === 0) return;
    this.syncing = true;
    try {
      const max = Math.min(500, this.config?.offline.maxBatchEvents ?? 500);
      while (!this.dead && this.queue.pending().length > 0) {
        const batch = this.queue.toBatch(`batch-${newId("checkin", { now: this.d.now() })}`, max);
        let res;
        try {
          res = await postSync(this.d.client, batch.batchId, batch.events);
        } catch (e) {
          this.queue.requeue(batch);
          if (this.dead) return;
          if (isUnreachable(e)) this.setOnline(false);
          else this.set({ syncError: isApiError(e) ? e.message : "Sync failed" });
          return;
        }
        const acked = new Set(res.acknowledged);
        const done = batch.events.filter((ev) => acked.has(ev.eventId));
        const left = batch.events.filter((ev) => !acked.has(ev.eventId));
        this.queue.acknowledge({ ...batch, events: done });
        if (left.length) this.queue.requeue({ ...batch, events: left });
        await this.persistQueue();
        this.set({ online: true, syncError: left.length ? "The server did not accept every scan" : null });
        if (done.length === 0) return; // would otherwise loop for ever
      }
    } finally {
      this.syncing = false;
    }
  }

  /* ---------- wall ---------- */
  private async refreshWallSecret(): Promise<void> {
    try {
      const s = await getWallSecret(this.d.client);
      this.noteServerTime(s.serverTime);
      const next: WallSecretCache = { kid: s.kid, kioskSecret: s.kioskSecret, notAfter: s.notAfter };
      const changed = !this.wallSecret || this.wallSecret.kid !== next.kid || this.wallSecret.kioskSecret !== next.kioskSecret;
      this.wallSecret = next;
      await this.d.store.write("wall", next);
      this.set({ online: true, syncError: null });
      if (changed) await this.tickWall();
    } catch (e) {
      if (this.dead) return;
      if (isUnreachable(e)) this.setOnline(false);
      else this.set({ syncError: isApiError(e) ? e.message : "Could not refresh the wall code" });
      if (!this.wallSecret) this.set({ wallProblem: "unavailable" });
    }
  }

  /** Renders the code for the current window and schedules the next one at the window boundary. */
  async tickWall(): Promise<void> {
    if (this.dead || this.d.credential.mode !== "wall") return;
    if (this.wallTimer) clearTimeout(this.wallTimer);
    const s = this.wallSecret;
    const { windowSeconds } = this.config?.window ?? DEFAULT_WINDOW;
    const now = this.d.now();
    if (!s) { this.set({ wall: null, wallProblem: "unavailable" }); return; }
    if (now > s.notAfter) {
      this.set({ wall: null, wallProblem: "expired" });
      // The key has ended: ask for the next one now, and once a window until it comes, not at the next 10-minute refresh.
      // A new secret re-enters `tickWall` (and clears this timer) from `refreshWallSecret`.
      this.wallTimer = setTimeout(() => void this.tickWall(), windowSeconds * 1000);
      void this.refreshWallSecret();
      return;
    }
    const win = windowIndex(now, windowSeconds);
    const code = await generateKioskCode(s.kid, s.kioskSecret, this.d.credential.branchId, this.d.credential.kioskId, now, windowSeconds);
    if (this.dead) return;
    const windowEndsAt = (win + 1) * windowSeconds * 1000;
    this.set({ wall: { code, windowEndsAt }, wallProblem: null });
    this.wallTimer = setTimeout(() => void this.tickWall(), Math.max(50, windowEndsAt - this.d.now() + 20));
  }
}

/** `HH:MM` at an instant in an IANA zone (Intl, no arithmetic). */
export function localTimeOf(epochMs: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit", numberingSystem: "latn" }).formatToParts(new Date(epochMs));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${hour.padStart(2, "0")}:${get("minute").padStart(2, "0")}`;
}

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
/** Same rule as `hoursAt` in `workers/api/src/kiosk/plans.ts`: the interval holding `time`, else the first; closed days read as closed; null hours are not enforced. */
export function hoursAt(weekly: WeeklyHours | null, onDate: string, time: string): BranchHours | null {
  if (!weekly) return null;
  const [y, m, d] = onDate.split("-").map(Number) as [number, number, number];
  const today = weekly[WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!] ?? [];
  const within = (iv: BranchHours) => (iv.open === iv.close ? true : iv.open < iv.close ? time >= iv.open && time < iv.close : time >= iv.open || time < iv.close);
  return today.find(within) ?? today[0] ?? { open: "24:00", close: "24:01" };
}

export { reasonText };
